/**
 * <s-carousel>
 * Swiper carousel. Dots and arrows are optional.
 * https://swiperjs.com/swiper-api#parameters
 *
 * {%- capture carousel_options -%}
 *   {
 *     "slidesPerView": "auto",
 *     "loop": false
 *   }
 * {%- endcapture -%}
 * <s-carousel data-options='{{ carousel_options }}' data-init="belowTablet">
 *   <div id="{{ custom_id }}" class="swiper" js-carousel>
 *     <div class="swiper-wrapper">
 *       <div class="swiper-slide" js-carousel-slide></div>
 *     </div>
 *     <div class="swiper-button-prev"></div>
 *     <div class="swiper-button-next"></div>
 *     <div class="swiper-pagination"></div>
 *   </div>
 * </s-carousel>
 *
 * data-options — JSON Swiper options. Single quotes outside, double quotes inside.
 * data-init — breakpoint name. The carousel is created while the query matches
 *             and destroyed when it does not.
 *             Max-width names (belowTablet) init on small screens.
 *             Min-width names (tablet) init on large screens.
 */

const mediaQueries = {
  landscapeAll: 'screen and (orientation: landscape)',
  landscape: 'screen and (orientation: landscape), screen and (min-width: 48rem)',
  belowTablet: 'screen and (max-width: 47.99rem)',
  tablet: 'screen and (min-width: 48rem)',
  belowLaptop: 'screen and (max-width: 63.99rem)',
  laptop: 'screen and (min-width: 64rem)',
  belowtabletl: 'screen and (max-width: 64rem)',
  tabletl: 'screen and (min-width: 64.01rem)',
  belowWidescreen: 'screen and (max-width: 79.99rem)',
  widescreen: 'screen and (min-width: 80rem)',
  belowExtrawide: 'screen and (max-width: 89.99rem)',
  extrawide: 'screen and (min-width: 90rem)',
}

class Carousel extends HTMLElement {
  constructor() {
    super()

    this.selectors = {
      carousel: '[js-carousel]',
    }

    this.options = {
      loop: true,
      slidesPerView: 'auto',
      grabCursor: true,
      centeredSlides: false,
      navigation: false,
      pagination: false,
      // Sideways trackpad swipes move the carousel. Up/down scrolling still scrolls the page, and a
      // swipe at either end stays with the carousel rather than triggering the browser's back gesture.
      mousewheel: {
        enabled: true,
        forceToAxis: true,
        releaseOnEdges: false,
      },
    }
  }

  connectedCallback() {
    if (this._initialised) return
    this._initialised = true

    const extendedOptions = this.dataset.options ? JSON.parse(this.dataset.options) : {}
    this.options = Object.assign(this.options, extendedOptions)
    this._initCarousel()
  }

  disconnectedCallback() {
    if (this._mediaQuery && this._onMediaChange) {
      this._mediaQuery.removeEventListener('change', this._onMediaChange)
    }
    this._destroyCarousel()
    this._initialised = false
  }

  _initCarousel() {
    const carouselEl = this.querySelector(this.selectors.carousel)
    if (!carouselEl || !carouselEl.id || typeof Swiper === 'undefined') return

    const id = `#${carouselEl.id}`
    const query = mediaQueries[this.dataset.init]

    if (this.dataset.init != null && query) {
      this._mediaQuery = window.matchMedia(query)
      this._onMediaChange = (event) => {
        if (event.matches) {
          this._createCarousel(id)
        } else {
          this._destroyCarousel()
        }
      }
      this._onMediaChange(this._mediaQuery)
      this._mediaQuery.addEventListener('change', this._onMediaChange)
      return
    }

    this._createCarousel(id)
  }

  _createCarousel(id) {
    if (this.carousel && !this.carousel.destroyed) return
    this.carousel = new Swiper(id, this.options)
  }

  _destroyCarousel() {
    if (this.carousel && this.carousel.el && this.carousel.el.classList.contains('swiper-initialized')) {
      this.carousel.destroy(true, true)
    }
  }
}

if (!customElements.get('s-carousel')) {
  customElements.define('s-carousel', Carousel)
}
